import { Button, Input, Menu, MenuItem, MenuTrigger } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import {
  type AuthAccountInfo,
  AuthService,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { getGroupsQuery, getMembersByWorkspaceIdQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { ImageIcon, InformationIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import {
  PdfSignatureCreator,
  type PdfSignatureCreatorResult,
} from '../../../../../blocksuite/attachment-viewer/pdf/pdf-signature-creator';
import * as pdfStyles from '../../../../../blocksuite/attachment-viewer/pdf/styles.css';
import { PermissionEditor } from '../../workspace-setting/shared/permission-editor';
import type {
  PermissionEditorAdapter,
  PermissionEditorConfig,
  PermissionEntry,
} from '../../workspace-setting/shared/permission-editor-types';
import { SettingsConfirmButton } from '../../workspace-setting/shared/settings-content';
import {
  getPdfSignatureErrorMessage,
  type SignatureAppearanceInfo,
  type SignatureProviderInfo,
  type SignatureRole,
  type SignatureRoleEntry,
  type SignatureSigningProfileInfo,
  type SignatureSigningProfileReadinessStatus,
  type SignatureTestSignStatus,
  useWorkspacePdfSignatures,
} from '../../workspace-setting/signatures/use-workspace-pdf-signatures';
import * as styles from './styles.css';

const PERSONAL_SIGNATURE_TYPES = new Set([
  'personal_signature',
  'drawn_signature',
]);

const SIGNATURE_PERMISSION_CONFIG: PermissionEditorConfig = {
  allRoles: ['Manager', 'Signer', 'Viewer'],
  grantableRoles: ['Manager', 'Signer', 'Viewer'],
  defaultGrantRole: 'Signer',
  testIdPrefix: 'pdf-signature-permissions',
};

interface SelectOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

function isPersonalSignatureAppearance(appearance: SignatureAppearanceInfo) {
  return PERSONAL_SIGNATURE_TYPES.has(appearance.type);
}

function getAccountDisplayName(account: AuthAccountInfo | null | undefined) {
  return (
    account?.label?.trim() ||
    account?.email?.trim() ||
    account?.id ||
    'Signature'
  );
}

function getTimestampLabel() {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date());
}

function toGrantableSignatureRole(
  role: string
): Exclude<SignatureRole, 'Owner'> {
  if (role === 'Manager' || role === 'Signer' || role === 'Viewer') {
    return role;
  }
  return 'Viewer';
}

function getReadinessLabel(
  t: ReturnType<typeof useI18n>,
  readinessStatus: SignatureSigningProfileReadinessStatus
) {
  switch (readinessStatus) {
    case 'not_configured':
      return t[
        'com.streamlain.pdf.signature.settings.digital.readiness.not_configured'
      ]();
    case 'auth_required':
      return t[
        'com.streamlain.pdf.signature.settings.digital.readiness.auth_required'
      ]();
    case 'identity_pending':
      return t[
        'com.streamlain.pdf.signature.settings.digital.readiness.identity_pending'
      ]();
    case 'certificate_ready':
      return t[
        'com.streamlain.pdf.signature.settings.digital.readiness.certificate_ready'
      ]();
    case 'test_sign_passed':
      return t[
        'com.streamlain.pdf.signature.settings.digital.readiness.test_sign_passed'
      ]();
    case 'expired':
      return t[
        'com.streamlain.pdf.signature.settings.digital.readiness.expired'
      ]();
    case 'revoked':
      return t[
        'com.streamlain.pdf.signature.settings.digital.readiness.revoked'
      ]();
    case 'disabled':
      return t[
        'com.streamlain.pdf.signature.settings.digital.readiness.disabled'
      ]();
    case 'provider_outage':
      return t[
        'com.streamlain.pdf.signature.settings.digital.readiness.provider_outage'
      ]();
  }
}

function getStatusLabel(
  t: ReturnType<typeof useI18n>,
  status:
    | SignatureSigningProfileInfo['status']
    | SignatureProviderInfo['status']
) {
  switch (status) {
    case 'active':
      return t['com.streamlain.pdf.signature.settings.status.active']();
    case 'disabled':
      return t['com.streamlain.pdf.signature.settings.status.disabled']();
    case 'unavailable':
      return t['com.streamlain.pdf.signature.settings.status.unavailable']();
  }
}

function getTestStatusLabel(
  t: ReturnType<typeof useI18n>,
  status: SignatureTestSignStatus
) {
  switch (status) {
    case 'not_run':
      return t['com.streamlain.pdf.signature.settings.status.notRun']();
    case 'running':
      return t['com.streamlain.pdf.signature.settings.status.running']();
    case 'passed':
      return t['com.streamlain.pdf.signature.settings.status.passed']();
    case 'failed':
      return t['com.streamlain.pdf.signature.settings.status.failed']();
  }
}

function SettingsSelect<T extends string>({
  value,
  options,
  onChange,
  disabled,
  dataTestId,
}: {
  value: T;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: T) => void;
  disabled?: boolean;
  dataTestId?: string;
}) {
  const current = options.find(option => option.value === value) ?? options[0];
  return (
    <Menu
      contentOptions={{ align: 'end' }}
      items={options.map(option => (
        <MenuItem
          key={option.value}
          disabled={option.disabled}
          onSelect={() => {
            if (!option.disabled) {
              onChange(option.value);
            }
          }}
          data-selected={option.value === value}
        >
          {option.label}
        </MenuItem>
      ))}
    >
      <MenuTrigger
        block
        className={styles.pdfSignatureSelectTrigger}
        disabled={disabled || options.every(option => option.disabled)}
        data-testid={dataTestId}
      >
        {current?.label ?? ''}
      </MenuTrigger>
    </Menu>
  );
}

function SignatureAccessEditor({
  appearance,
  listRoles,
  assignRole,
  removeRole,
}: {
  appearance: SignatureAppearanceInfo;
  listRoles: (appearanceId: string) => Promise<SignatureRoleEntry[]>;
  assignRole: (input: {
    appearanceId: string;
    type: 'user' | 'group';
    targetId: string;
    role: Exclude<SignatureRole, 'Owner'>;
  }) => Promise<SignatureRoleEntry[]>;
  removeRole: (appearanceId: string, roleId: string) => Promise<void>;
}) {
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const workspaceId = workspaceService.workspace.id;
  const permissionAdapter = useMemo(
    (): PermissionEditorAdapter => ({
      async loadEntries(): Promise<PermissionEntry[]> {
        const roles = await listRoles(appearance.id);
        return roles.map(role => {
          if (role.type === 'user') {
            return {
              type: 'user' as const,
              id: role.id,
              targetId: role.targetId,
              name: role.targetName ?? '',
              email: role.targetEmail ?? '',
              avatarUrl: role.targetAvatarUrl ?? null,
              role: role.role,
            };
          }
          return {
            type: 'group' as const,
            id: role.id,
            targetId: role.targetId,
            name: role.targetName,
            role: role.role,
          };
        });
      },
      async loadMembers(query: string) {
        if (!serverService.server) return [];
        const result = await serverService.server.gql({
          query: getMembersByWorkspaceIdQuery,
          variables: {
            workspaceId,
            take: 50,
            query: query || undefined,
          },
        });
        return (result.workspace?.members ?? []).map(member => ({
          id: member.id,
          name: member.name ?? '',
          email: member.email ?? '',
          avatarUrl: member.avatarUrl ?? null,
        }));
      },
      async loadGroups() {
        if (!serverService.server) return [];
        const result = await serverService.server.gql({
          query: getGroupsQuery,
          variables: { workspaceId },
        });
        return (result.groups ?? []).map(
          (group: {
            id: string;
            name: string;
            description?: string | null;
          }) => ({
            id: group.id,
            name: group.name,
            description: group.description ?? undefined,
          })
        );
      },
      async grantUser(userId: string, role: string) {
        await assignRole({
          appearanceId: appearance.id,
          type: 'user',
          targetId: userId,
          role: toGrantableSignatureRole(role),
        });
      },
      async grantGroup(groupId: string, role: string) {
        await assignRole({
          appearanceId: appearance.id,
          type: 'group',
          targetId: groupId,
          role: toGrantableSignatureRole(role),
        });
      },
      async updateRole(entry: PermissionEntry, newRole: string) {
        await assignRole({
          appearanceId: appearance.id,
          type: entry.type,
          targetId: entry.targetId,
          role: toGrantableSignatureRole(newRole),
        });
      },
      async revoke(entry: PermissionEntry) {
        await removeRole(appearance.id, entry.id);
      },
    }),
    [
      appearance.id,
      assignRole,
      listRoles,
      removeRole,
      serverService.server,
      workspaceId,
    ]
  );

  return (
    <PermissionEditor
      config={SIGNATURE_PERMISSION_CONFIG}
      adapter={permissionAdapter}
    />
  );
}

function SignatureAppearanceCard({
  appearance,
  imageUrl,
  active,
}: {
  appearance: SignatureAppearanceInfo;
  imageUrl: string;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      className={pdfStyles.pdfSignatureAppearanceCard}
      data-active={active ? 'true' : undefined}
      aria-label={appearance.ownerDisplayName}
      title={appearance.ownerDisplayName}
    >
      <img alt="" src={imageUrl} />
      <span>{appearance.ownerDisplayName}</span>
    </button>
  );
}

function DigitalSignatureProfileCard({
  profile,
  disabled,
  onTest,
  onSetDefault,
  onEnable,
  onDisable,
}: {
  profile: SignatureSigningProfileInfo;
  disabled?: boolean;
  onTest: (profileId: string) => void;
  onSetDefault: (profileId: string) => void;
  onEnable: (profileId: string) => void;
  onDisable: (profileId: string) => void;
}) {
  const t = useI18n();
  const isActive = profile.status === 'active';
  const isReady = profile.readinessStatus === 'test_sign_passed';
  const providerName =
    profile.providerName ??
    t['com.streamlain.pdf.signature.digital.providerFallback']();
  const certificateName =
    profile.certificateName ??
    t['com.streamlain.pdf.signature.digital.certificateFallback']();

  return (
    <article className={styles.pdfSignatureDigitalProfileCard}>
      <div className={styles.pdfSignatureDigitalProfileHeader}>
        <div className={styles.pdfSignatureDigitalProfileText}>
          <div className={styles.pdfSignatureDigitalProfileTitle}>
            {profile.name}
          </div>
          <div className={styles.pdfSignatureDigitalProfileMeta}>
            {providerName} · {certificateName}
          </div>
        </div>
        <div className={styles.pdfSignatureBadgeRow}>
          <span
            className={styles.pdfSignatureStatusBadge}
            data-variant={isActive ? 'active' : 'soft'}
          >
            {getStatusLabel(t, profile.status)}
          </span>
          {profile.isDefault ? (
            <span
              className={styles.pdfSignatureStatusBadge}
              data-variant="active"
            >
              {t['com.streamlain.pdf.signature.settings.default']()}
            </span>
          ) : null}
        </div>
      </div>
      <div className={styles.pdfSignatureDigitalProfileBody}>
        <span>{getReadinessLabel(t, profile.readinessStatus)}</span>
        <span>
          {t['com.streamlain.pdf.signature.settings.digital.testStatus']({
            status: getTestStatusLabel(t, profile.testSignStatus),
          })}
        </span>
        {profile.failureReason ? <span>{profile.failureReason}</span> : null}
      </div>
      <div className={styles.pdfSignatureActions}>
        <Button
          variant="secondary"
          disabled={disabled || !isActive}
          onClick={() => onTest(profile.id)}
        >
          {t['com.streamlain.pdf.signature.settings.digital.test']()}
        </Button>
        <Button
          variant="secondary"
          disabled={disabled || !isActive || profile.isDefault || !isReady}
          onClick={() => onSetDefault(profile.id)}
        >
          {profile.isDefault
            ? t['com.streamlain.pdf.signature.settings.default']()
            : t['com.streamlain.pdf.signature.settings.setDefault']()}
        </Button>
        {isActive ? (
          <SettingsConfirmButton
            label={t['com.streamlain.pdf.signature.settings.digital.disable']()}
            confirmLabel={t[
              'com.streamlain.pdf.signature.settings.digital.disableConfirm'
            ]()}
            cancelLabel={t['com.streamlain.pdf.signature.settings.cancel']()}
            disabled={disabled}
            loading={disabled}
            onConfirm={() => onDisable(profile.id)}
            variant="secondary"
          />
        ) : (
          <Button
            variant="secondary"
            disabled={disabled}
            onClick={() => onEnable(profile.id)}
          >
            {t['com.streamlain.pdf.signature.settings.digital.enable']()}
          </Button>
        )}
      </div>
    </article>
  );
}

export function PdfSignatureSettingsSection() {
  const t = useI18n();
  const account = useLiveData(useService(AuthService).session.account$);
  const workspaceService = useService(WorkspaceService);
  const signatures = useWorkspacePdfSignatures();
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [sharingOpen, setSharingOpen] = useState(false);
  const [digitalSetupOpen, setDigitalSetupOpen] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileProviderId, setProfileProviderId] = useState('');
  const [profileCertificateRefId, setProfileCertificateRefId] = useState('');

  const ownPersonalSignature = useMemo(
    () =>
      signatures.appearances.find(
        appearance =>
          isPersonalSignatureAppearance(appearance) &&
          appearance.status === 'active' &&
          (appearance.ownerUserId === account?.id ||
            appearance.role === 'Owner')
      ) ?? null,
    [account?.id, signatures.appearances]
  );

  const sharedAppearances = useMemo(
    () =>
      signatures.appearances.filter(
        appearance =>
          isPersonalSignatureAppearance(appearance) &&
          appearance.status === 'active' &&
          appearance.canUse &&
          appearance.id !== ownPersonalSignature?.id &&
          appearance.ownerUserId !== account?.id
      ),
    [account?.id, ownPersonalSignature?.id, signatures.appearances]
  );

  const policyAllowedProviderIds = useMemo(
    () => signatures.policy?.allowedProviderIds ?? [],
    [signatures.policy?.allowedProviderIds]
  );
  const activeDigitalProviders = useMemo(
    () =>
      signatures.providers.filter(
        provider =>
          provider.status === 'active' &&
          (policyAllowedProviderIds.length === 0 ||
            policyAllowedProviderIds.includes(provider.id))
      ),
    [policyAllowedProviderIds, signatures.providers]
  );
  const selectedProviderId =
    profileProviderId || activeDigitalProviders[0]?.id || '';
  const profileCertificateOptions = useMemo(
    () =>
      signatures.certificates.filter(
        certificate =>
          certificate.status === 'active' &&
          (!selectedProviderId || certificate.providerId === selectedProviderId)
      ),
    [selectedProviderId, signatures.certificates]
  );
  const selectedCertificateRefId =
    profileCertificateRefId || profileCertificateOptions[0]?.id || '';
  const providerOptions = useMemo<Array<SelectOption<string>>>(
    () =>
      activeDigitalProviders.length
        ? activeDigitalProviders.map(provider => ({
            value: provider.id,
            label: provider.name,
          }))
        : [
            {
              value: '',
              label:
                t[
                  'com.streamlain.pdf.signature.settings.digital.providerUnavailable'
                ](),
              disabled: true,
            },
          ],
    [activeDigitalProviders, t]
  );
  const certificateOptions = useMemo<Array<SelectOption<string>>>(
    () =>
      profileCertificateOptions.length
        ? profileCertificateOptions.map(certificate => ({
            value: certificate.id,
            label: certificate.name,
          }))
        : [
            {
              value: '',
              label:
                t[
                  'com.streamlain.pdf.signature.settings.digital.certificateUnavailable'
                ](),
              disabled: true,
            },
          ],
    [profileCertificateOptions, t]
  );
  const digitalProfiles = signatures.profiles;
  const canCreateDigitalProfile = Boolean(
    selectedProviderId && selectedCertificateRefId
  );

  const resolveClipboardBlob = useCallback(
    async (blobId: string) =>
      workspaceService.workspace.docCollection.blobSync.get(blobId),
    [workspaceService]
  );

  const createPersonalSignature = useCallback(
    async ({ file }: PdfSignatureCreatorResult) => {
      try {
        await signatures.uploadAppearance({
          name: t['com.streamlain.pdf.signature.settings.autoName']({
            name: getAccountDisplayName(account),
            date: getTimestampLabel(),
          }),
          type: 'personal_signature',
          file,
        });
        setCreatorOpen(false);
        setSharingOpen(false);
        return true;
      } catch (error) {
        console.error('Failed to upload PDF signature appearance', error);
        return false;
      }
    },
    [account, signatures, t]
  );

  const createDigitalSignatureProfile = useCallback(async () => {
    if (!selectedProviderId || !selectedCertificateRefId) {
      return;
    }
    try {
      await signatures.createSigningProfile({
        name:
          profileName.trim() ||
          t['com.streamlain.pdf.signature.settings.digital.autoName']({
            name: getAccountDisplayName(account),
            date: getTimestampLabel(),
          }),
        providerId: selectedProviderId,
        certificateRefId: selectedCertificateRefId,
      });
      setProfileName('');
      setProfileCertificateRefId('');
      setDigitalSetupOpen(false);
    } catch (error) {
      console.error('Failed to create PDF signing profile', error);
    }
  }, [
    account,
    profileName,
    selectedCertificateRefId,
    selectedProviderId,
    signatures,
    t,
  ]);

  const loadErrorMessage = signatures.loadError
    ? getPdfSignatureErrorMessage(signatures.loadError, 'load')
    : null;

  return (
    <>
      <SettingHeader
        title={t['com.streamlain.pdf.signature.settings.title']()}
        subtitle={t['com.streamlain.pdf.signature.settings.subtitle']()}
      />
      <SettingWrapper id="pdf-signatures">
        <SettingRow
          name={t['com.streamlain.pdf.signature.settings.mine.title']()}
          desc={t['com.streamlain.pdf.signature.settings.mine.description']()}
          spreadCol={false}
        >
          <div className={styles.pdfSignaturePanel}>
            {loadErrorMessage ? (
              <div className={pdfStyles.pdfSignatureInlineStatus}>
                <InformationIcon aria-hidden />
                <span>{loadErrorMessage}</span>
              </div>
            ) : null}

            <div className={pdfStyles.pdfSignatureAppearanceList}>
              {ownPersonalSignature ? (
                <SignatureAppearanceCard
                  appearance={ownPersonalSignature}
                  imageUrl={signatures.appearanceImageUrl(
                    ownPersonalSignature.id
                  )}
                  active
                />
              ) : (
                <div className={pdfStyles.pdfSignatureEmptyState}>
                  <ImageIcon aria-hidden />
                  <span>
                    {t['com.streamlain.pdf.signature.settings.mine.empty']()}
                  </span>
                </div>
              )}
            </div>

            <button
              type="button"
              className={pdfStyles.pdfSignatureCreateButton}
              aria-expanded={creatorOpen}
              onClick={() => setCreatorOpen(open => !open)}
            >
              <span>
                {ownPersonalSignature
                  ? t['com.streamlain.pdf.signature.settings.replace']()
                  : t['com.streamlain.pdf.signature.settings.create']()}
              </span>
            </button>

            {creatorOpen ? (
              <div className={styles.pdfSignatureCreatorHost}>
                <PdfSignatureCreator
                  isSaving={signatures.isSaving}
                  onCreate={createPersonalSignature}
                  resolveClipboardBlob={resolveClipboardBlob}
                />
              </div>
            ) : null}

            {ownPersonalSignature ? (
              <div className={styles.pdfSignatureActions}>
                <Button
                  variant="secondary"
                  onClick={() => setSharingOpen(open => !open)}
                >
                  {t['com.streamlain.pdf.signature.settings.share']()}
                </Button>
                <SettingsConfirmButton
                  label={t['com.streamlain.pdf.signature.settings.delete']()}
                  confirmLabel={t[
                    'com.streamlain.pdf.signature.settings.deleteConfirm'
                  ]()}
                  cancelLabel={t[
                    'com.streamlain.pdf.signature.settings.cancel'
                  ]()}
                  disabled={signatures.isSaving}
                  loading={signatures.isSaving}
                  onConfirm={() => {
                    void signatures.deleteAppearance(ownPersonalSignature.id);
                    setSharingOpen(false);
                  }}
                />
              </div>
            ) : null}

            {ownPersonalSignature && sharingOpen ? (
              <div className={styles.pdfSignatureAccess}>
                <div className={styles.pdfSignatureSubsectionTitle}>
                  {t['com.streamlain.pdf.signature.settings.share.title']()}
                </div>
                <SignatureAccessEditor
                  appearance={ownPersonalSignature}
                  listRoles={signatures.listAppearanceRoles}
                  assignRole={signatures.assignAppearanceRole}
                  removeRole={signatures.removeAppearanceRole}
                />
              </div>
            ) : null}

            {sharedAppearances.length > 0 ? (
              <div className={styles.pdfSignatureSubsection}>
                <div className={styles.pdfSignatureSubsectionTitle}>
                  {t['com.streamlain.pdf.signature.settings.shared.title']()}
                </div>
                <div className={pdfStyles.pdfSignatureAppearanceList}>
                  {sharedAppearances.map(appearance => (
                    <SignatureAppearanceCard
                      key={appearance.id}
                      appearance={appearance}
                      imageUrl={signatures.appearanceImageUrl(appearance.id)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </SettingRow>
        <SettingRow
          name={t['com.streamlain.pdf.signature.settings.digital.title']()}
          desc={t[
            'com.streamlain.pdf.signature.settings.digital.description'
          ]()}
          spreadCol={false}
        >
          <div className={styles.pdfSignaturePanel}>
            {loadErrorMessage ? (
              <div className={pdfStyles.pdfSignatureInlineStatus}>
                <InformationIcon aria-hidden />
                <span>{loadErrorMessage}</span>
              </div>
            ) : null}

            {!loadErrorMessage && digitalProfiles.length === 0 ? (
              <div className={pdfStyles.pdfSignatureEmptyState}>
                <InformationIcon aria-hidden />
                <span>
                  {canCreateDigitalProfile
                    ? t[
                        'com.streamlain.pdf.signature.settings.digital.notConfigured'
                      ]()
                    : t[
                        'com.streamlain.pdf.signature.settings.digital.adminRequired'
                      ]()}
                </span>
              </div>
            ) : null}

            {digitalProfiles.length > 0 ? (
              <div className={styles.pdfSignatureDigitalProfileList}>
                {digitalProfiles.map(profile => (
                  <DigitalSignatureProfileCard
                    key={profile.id}
                    profile={profile}
                    disabled={signatures.isSaving}
                    onTest={profileId => {
                      void signatures.testSigningProfile(profileId);
                    }}
                    onSetDefault={profileId => {
                      void signatures.setDefaultSigningProfile(profileId);
                    }}
                    onEnable={profileId => {
                      void signatures.updateSigningProfile(profileId, {
                        status: 'active',
                      });
                    }}
                    onDisable={profileId => {
                      void signatures.deleteSigningProfile(profileId);
                    }}
                  />
                ))}
              </div>
            ) : null}

            <button
              type="button"
              className={pdfStyles.pdfSignatureCreateButton}
              aria-expanded={digitalSetupOpen}
              disabled={!!loadErrorMessage || !canCreateDigitalProfile}
              onClick={() => setDigitalSetupOpen(open => !open)}
            >
              <span>
                {t['com.streamlain.pdf.signature.settings.digital.configure']()}
              </span>
            </button>

            {digitalSetupOpen ? (
              <div className={styles.pdfSignatureDigitalSetup}>
                <Input
                  value={profileName}
                  onChange={setProfileName}
                  placeholder={t[
                    'com.streamlain.pdf.signature.settings.digital.profileName'
                  ]()}
                />
                <div className={styles.pdfSignatureDigitalSetupGrid}>
                  <SettingsSelect
                    value={selectedProviderId}
                    options={providerOptions}
                    disabled={signatures.isSaving}
                    dataTestId="pdf-signature-profile-provider"
                    onChange={value => {
                      setProfileProviderId(value);
                      setProfileCertificateRefId('');
                    }}
                  />
                  <SettingsSelect
                    value={selectedCertificateRefId}
                    options={certificateOptions}
                    disabled={signatures.isSaving || !selectedProviderId}
                    dataTestId="pdf-signature-profile-certificate"
                    onChange={setProfileCertificateRefId}
                  />
                </div>
                <div className={styles.pdfSignatureActions}>
                  <Button
                    variant="primary"
                    disabled={signatures.isSaving || !canCreateDigitalProfile}
                    onClick={() => void createDigitalSignatureProfile()}
                  >
                    {t['com.streamlain.pdf.signature.settings.digital.save']()}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={signatures.isSaving}
                    onClick={() => setDigitalSetupOpen(false)}
                  >
                    {t['com.streamlain.pdf.signature.settings.cancel']()}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </SettingRow>
      </SettingWrapper>
    </>
  );
}
